import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getThemeById } from '../../config/themes'
import { canRoundAcceptBet, isRoundNotYetOpen, isRoundCancelled, getLottoRoundClosedCardClass, getLottoRoundStatusColorClass, getRoundImage, lotteryService, type LotteryRound, type PublicHuayType } from '../../services/lotteryService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import { lookupHuayYoutubeLiveForRound, type HuayYoutubeLiveConfig } from '../../lib/liveTv'
import HuayLiveModal from '../../components/HuayLiveModal'
import HuayLiveBadge from '../../components/HuayLiveBadge'
import './ModernLobbyHuayListAllPage.css'

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const CARD_COL = 'col-12 col-md-6 px-1 lobby-huay-col'
const CARD_COL_GRID = 'col-12 col-sm-6 col-lg-4 col-xl-3 px-1 lobby-huay-col'
const CARD_STACK = 'd-grid gap-2 lobby-huay-col-stack'
const CARD_BASE = 'hh_hover lobby-huay-round-card'

const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const el = e.currentTarget
  if (el.dataset.imgFallback === '1') return
  el.dataset.imgFallback = '1'
  el.src = IMG_PLACEHOLDER
}

type RoundCardBadgeTone = 'open' | 'closed' | 'pending' | 'cancel'

function getRoundBadge(
  round: Pick<LotteryRound, 'status'>,
  active: boolean,
  notYetOpen: boolean
): { text: string; tone: RoundCardBadgeTone } {
  if (isRoundCancelled(round)) return { text: 'ยกเลิก', tone: 'cancel' }
  if (notYetOpen) return { text: 'ยังไม่เปิด', tone: 'pending' }
  if (active) return { text: 'เปิดรับ', tone: 'open' }
  return { text: 'ปิดรับ', tone: 'closed' }
}

type RoundBetCardProps = {
  title: string
  imgSrc?: string
  badge: { text: string; tone: RoundCardBadgeTone }
  leftLabel: string
  leftValue: React.ReactNode
  leftClassName?: string
  rightLabel: string
  rightValue: React.ReactNode
  closedCardClass?: string
  isLastMinutes?: boolean
  onClick?: () => void
  liveBadge?: React.ReactNode
  asLink?: string
}

const RoundBetCard: React.FC<RoundBetCardProps> = ({
  title,
  imgSrc,
  badge,
  leftLabel,
  leftValue,
  leftClassName = '',
  rightLabel,
  rightValue,
  closedCardClass = '',
  isLastMinutes = false,
  onClick,
  liveBadge,
  asLink,
}) => {
  const inner = (
    <div
      role={asLink ? undefined : 'button'}
      tabIndex={asLink ? undefined : 0}
      className={`${CARD_BASE} ${closedCardClass}${badge.tone === 'open' ? ' is-open' : ''}${isLastMinutes ? ' is-wip' : ''}`}
      style={{ cursor: 'pointer', position: 'relative' }}
      onClick={asLink ? undefined : onClick}
      onKeyDown={
        asLink
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
      }
    >
      {liveBadge}
      <header className="lobby-huay-round-card__head">
        <div className="lobby-huay-round-card__identity">
          {imgSrc ? (
            <div className={`lobby-huay-round-card__icon${isLastMinutes ? ' wip_wup' : ''}`}>
              <img src={imgSrc} alt="" onError={handleImgError} />
            </div>
          ) : null}
          <h6 className={`lobby-huay-round-card__title${isLastMinutes ? ' wip_wup' : ''}`} title={title}>
            {title}
          </h6>
        </div>
        <span className={`lobby-huay-round-card__badge is-${badge.tone}${isLastMinutes ? ' wip_wup' : ''}`}>
          {badge.text}
        </span>
      </header>
      <div className="lobby-huay-round-card__metrics">
        <div className="lobby-huay-round-card__metric">
          <small>{leftLabel}</small>
          <strong className={`lobby-huay-round-card__countdown ${leftClassName}${isLastMinutes ? ' wip_wup' : ''}`}>
            {leftValue}
          </strong>
        </div>
        <div className="lobby-huay-round-card__metric">
          <small>{rightLabel}</small>
          <strong className="lobby-huay-round-card__footer-value">{rightValue}</strong>
        </div>
      </div>
    </div>
  )

  if (asLink) {
    return (
      <Link to={asLink} className="text-decoration-none lobby-huay-round-card-link">
        {inner}
      </Link>
    )
  }
  return inner
}

/** แสดง countdown ที่อัปเดตทุกวินาที — ยังไม่เปิด: นับถอยหลังถึงเวลาเปิด, เปิดอยู่: นับถึงเวลาปิด, ยกเลิก/ปิดแล้ว: ข้อความสถานะ */
const computeCountdownText = (round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>) => {
  if (isRoundCancelled(round)) return 'ยกเลิก'
  if (isRoundNotYetOpen(round)) {
    return lotteryService.formatTimeRemaining(round.betStartTime)
  }
  return canRoundAcceptBet(round) ? lotteryService.formatTimeRemaining(round.betEndTime) : 'ปิดรับแทง'
}

const CountdownDisplay: React.FC<{ round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'> }> = ({ round }) => {
  const [text, setText] = useState(() => computeCountdownText(round))

  useEffect(() => {
    const tick = () => {
      setText(computeCountdownText(round))
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [round])

  return <>{text}</>
}

function normalizeLabel(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[().]/g, '')
    .toLowerCase()
}

function formatCloseTime(betEndTime: string): string {
  const betEndDate = new Date(betEndTime)
  // Compact for mobile 2-col cards (skip year — cards already feel cramped)
  return betEndDate.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** หน้ารายการหวยวันนี้ (listall) — ใช้ API และ logic เดียวกับ user-classic /lotto, ต่างเฉพาะ UI แบบ modern */
const ModernLobbyHuayListAllPage: React.FC = () => {
  const { themeConfig, assetConfig } = useOutletContext<LobbyHuayOutletContext>()
  const location = useLocation()
  const navigate = useNavigate()
  const fromWinNumber = (location.state as { fromWinNumber?: unknown[] } | null)?.fromWinNumber
  const wipWupMinutes = themeConfig?.settings?.wipWupMinutes ?? 15
  const wipWupMinutesOther = themeConfig?.settings?.wipWupMinutesOther ?? 15
  const themeFolder = useMemo(
    () => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue',
    [themeConfig.themeId]
  )
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [huayTypes, setHuayTypes] = useState<PublicHuayType[]>([])
  const [loading, setLoading] = useState(true)
  const isInitialLoadRef = useRef(true)
  const realtimeSyncTimeoutRef = useRef<number | null>(null)
  /** ประเภทหวยที่แอดมินปิด (จาก WebSocket huay_type_updated) — ซ่อน section ทั้งบล็อก */
  const [disabledHuayTypeNames, setDisabledHuayTypeNames] = useState<Set<string>>(() => new Set())
  /** Live YouTube popup state สำหรับการ์ดหวย */
  const huayYoutubeLive = themeConfig?.settings?.huayYoutubeLive
  const [livePopup, setLivePopup] = useState<{ title: string; youtubeUrl: string } | null>(null)
  const openLivePopup = useCallback((round: LotteryRound, entry: HuayYoutubeLiveConfig) => {
    setLivePopup({
      title: entry.label?.trim() || round.huayName || 'ถ่ายทอดสด',
      youtubeUrl: entry.youtubeUrl,
    })
  }, [])
  const closeLivePopup = useCallback(() => setLivePopup(null), [])

  /** เมื่อเข้าหน้านี้ (เช่น มาจากปุ่ม "แทงหวยเลย") ให้เลื่อนไปด้านบน ไม่ใช้ตำแหน่ง scroll เดิม */
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const getAssetUrl = useCallback(
    (path: string) => {
      const base = assetConfig.CDN_BASE_URL
        ? `${assetConfig.CDN_BASE_URL}/${themeFolder}`
        : `/${themeFolder}`
      return base + (path.startsWith('/') ? path : `/${path}`)
    },
    [assetConfig.CDN_BASE_URL, themeFolder]
  )

  /** ถ้าเป็น URL เต็ม (http/https) ใช้ตามนั้น ไม่ต้องใส่ theme path */
  const getRoundImageSrc = useCallback(
    (round: LotteryRound) => {
      const u = getRoundImage(round)
      if (/^https?:\/\//i.test(u)) return u
      return getAssetUrl(u)
    },
    [getAssetUrl]
  )

  const resolveImageSrc = useCallback(
    (value: string | null | undefined, fallbackAsset: string) => {
      const trimmed = String(value || '').trim()
      if (trimmed) {
        if (/^https?:\/\//i.test(trimmed)) return trimmed
        if (trimmed.startsWith('/')) return getAssetUrl(trimmed)
        return trimmed
      }
      return getAssetUrl(fallbackAsset)
    },
    [getAssetUrl]
  )

  const loadRounds = useCallback(async (cacheBust = false) => {
    const showLoading = isInitialLoadRef.current
    if (showLoading) {
      isInitialLoadRef.current = false
      setLoading(true)
    }
    try {
      const data = await lotteryService.getTodayRounds(cacheBust)
      setRounds(data)
    } catch (err) {
      console.error('Error loading rounds:', err)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  const loadHuayTypes = useCallback(async () => {
    try {
      const data = await lotteryService.getTenantHuayTypes({ limit: 200 })
      setHuayTypes(data)
    } catch (err) {
      console.error('Error loading huay types:', err)
    }
  }, [])

  const pollIntervalMs = typeof import.meta.env.DEV !== 'undefined' && import.meta.env.DEV ? 15_000 : 5_000
  useEffect(() => {
    loadRounds()
    loadHuayTypes()
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        loadRounds(true)
      }
    }, pollIntervalMs)
    return () => clearInterval(id)
  }, [loadRounds, loadHuayTypes, pollIntervalMs])

  const handleRoundRealtimeUpdate = useCallback((event?: Event) => {
    const detail = (event as CustomEvent<Record<string, unknown> | undefined> | undefined)?.detail
    const type =
      typeof detail?.type === 'string'
        ? detail.type
        : typeof detail?.sourceType === 'string'
          ? detail.sourceType
          : ''
    const roundDetail = (detail?.round ?? detail?.data) as LotteryRound | undefined

    if (roundDetail?._id) {
      setRounds((prev) => {
        const index = prev.findIndex((round) => round._id === roundDetail._id)
        if (index === -1) return prev

        const next = [...prev]
        next[index] = {
          ...next[index],
          ...roundDetail,
        }
        return next
      })
      if (realtimeSyncTimeoutRef.current) {
        window.clearTimeout(realtimeSyncTimeoutRef.current)
      }
      // Do not refetch immediately: some public round reads can lag behind the websocket payload
      // and would overwrite the fresh realtime state with stale data.
      realtimeSyncTimeoutRef.current = window.setTimeout(() => {
        loadRounds(true)
      }, 5000)
      return
    }

    if (!type || type === 'lottery_round_updated' || type === 'lottery_round_update') {
      loadRounds(true)
    }
  }, [loadRounds])

  useEffect(() => {
    const handleHuayTypeUpdated = (e: Event) => {
      const detail = (e as CustomEvent<{ name?: string; isActive?: boolean }>)?.detail
      const name = detail?.name
      if (name) {
        setDisabledHuayTypeNames((prev) => {
          const next = new Set(prev)
          if (detail.isActive === false) next.add(name)
          else if (detail.isActive === true) next.delete(name)
          return next
        })
      }
      loadHuayTypes()
      loadRounds(true)
    }
    window.addEventListener('lottery_round_updated', handleRoundRealtimeUpdate)
    window.addEventListener('lottery_round_update', handleRoundRealtimeUpdate)
    window.addEventListener('content_update', handleRoundRealtimeUpdate)
    window.addEventListener('huay_type_updated', handleHuayTypeUpdated)
    return () => {
      if (realtimeSyncTimeoutRef.current) {
        window.clearTimeout(realtimeSyncTimeoutRef.current)
      }
      window.removeEventListener('lottery_round_updated', handleRoundRealtimeUpdate)
      window.removeEventListener('lottery_round_update', handleRoundRealtimeUpdate)
      window.removeEventListener('content_update', handleRoundRealtimeUpdate)
      window.removeEventListener('huay_type_updated', handleHuayTypeUpdated)
    }
  }, [handleRoundRealtimeUpdate, loadHuayTypes, loadRounds])

  const huayTypeImageMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const huayType of huayTypes) {
      const image = String(huayType.imageUrl || huayType.imageURL || '').trim()
      if (!image) continue
      if (huayType.name) map.set(normalizeLabel(huayType.name), image)
      if (huayType.code) map.set(normalizeLabel(huayType.code), image)
    }
    return map
  }, [huayTypes])

  const getHuayTypeImageSrc = useCallback(
    (aliases: string[], fallbackAsset: string) => {
      for (const alias of aliases) {
        const matched = huayTypeImageMap.get(normalizeLabel(alias))
        if (matched) {
          return resolveImageSrc(matched, fallbackAsset)
        }
      }
      return getAssetUrl(fallbackAsset)
    },
    [getAssetUrl, huayTypeImageMap, resolveImageSrc]
  )

  // เรียงลำดับ: เปิดรับ (เรียงตามเวลาปิดรับ) → ยังไม่เปิด → ปิดรับแล้ว (ท้ายสุด)
  const roundsByType = useMemo(() => {
    const grouped = lotteryService.groupRoundsByType(rounds)
    for (const key of Object.keys(grouped)) {
      grouped[key] = lotteryService.sortRoundsForLottoDisplay(grouped[key])
    }
    return grouped
  }, [rounds])

  /** ประเภทที่มี section เฉพาะแล้ว — ประเภทที่เหลือจาก API จะไปแสดงในบล็อก dynamic ด้านล่าง */
  const KNOWN_SECTION_TYPE_NAMES = useMemo(
    () =>
      new Set([
        'หวยรัฐบาลไทย',
        'หวยยี่กี',
        'หวยยีกี่',
        'ยี่กี',
        'ยีกี่',
        'หวยต่างประเทศ',
        'หวยหุ้น VIP',
        'หวย VIP',
        'หวยหุ้น',
        'หวยชุด',
        'หวย VISA',
      ]),
    []
  )
  const otherTypeNames = useMemo(
    () => Object.keys(roundsByType).filter((k) => !KNOWN_SECTION_TYPE_NAMES.has(k)),
    [roundsByType, KNOWN_SECTION_TYPE_NAMES]
  )

  const govRounds = useMemo(
    () =>
      (roundsByType['หวยรัฐบาลไทย'] || []).filter(
        (r) =>
          r.huayName.includes('รัฐบาลไทย') &&
          !r.huayName.includes('ออมสิน') &&
          !r.huayName.includes('ธกส') &&
          !r.huayName.includes('ธ.ก.ส.')
      ),
    [roundsByType]
  )

  const yeekeeCount = useMemo(
    () =>
      (
        roundsByType['หวยยี่กี'] ||
        roundsByType['หวยยีกี่'] ||
        roundsByType['ยี่กี'] ||
        roundsByType['ยีกี่'] ||
        []
      ).length,
    [roundsByType]
  )

  const bankConfigs = useMemo(
    () => [
      { searchNames: ['ธ.ก.ส.', 'ธกส'], displayName: 'หวย ธ.ก.ส.', img: '/img/flag/egov.png' },
      { searchNames: ['ออมสิน'], displayName: 'หวยออมสิน', img: '/img/flag/gsb.png' },
    ],
    []
  )

  const governmentTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยรัฐบาลไทย', 'หวยรัฐบาล'], '/img/flag/th.png'),
    [getHuayTypeImageSrc]
  )
  const yeekeeTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยยี่กี', 'หวยยีกี่', 'ยี่กี', 'ยีกี่', 'yeekee'], '/img/yk.jpg'),
    [getHuayTypeImageSrc]
  )
  const bankSectionImage = governmentTypeImage
  const foreignTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยต่างประเทศ'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const vipTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยหุ้น VIP', 'หวย VIP'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const stockTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยหุ้น', 'หวยหุ้นไทย', 'หวยหุ้นต่างประเทศ'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const setTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวยชุด'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )
  const visaTypeImage = useMemo(
    () => getHuayTypeImageSrc(['หวย VISA'], '/img/logo-t.png'),
    [getHuayTypeImageSrc]
  )

  const handleRoundClick = useCallback(
    (round: LotteryRound, isYeekeeName: boolean) => {
      if (isRoundNotYetOpen(round)) {
        void Swal.fire({
          icon: 'info',
          title: 'ยังไม่เปิดรับแทง รอเวลาเปิด',
          toast: true,
          position: 'top',
          timer: 2500,
          showConfirmButton: false,
        })
        return
      }
      const active = canRoundAcceptBet(round)
      if (active) {
        navigate('/lobby-huay/lottery-terms', { state: { round, ...(fromWinNumber && { fromWinNumber }) } })
      } else if (isYeekeeName) {
        navigate(`/lobby-huay/yeekee/result?roundId=${round._id}`, { state: { round } })
      } else {
        navigate(`/lobby-huay/result?roundId=${round._id}`, { state: { round, isYeekeeName } })
      }
    },
    [navigate, fromWinNumber]
  )

  /** การ์ดรอบหวย สำหรับ หวยธนาคาร */
  const renderRoundCard = (
    round: LotteryRound,
    key: string,
    opts: { isYeekeeName?: boolean; timeClass?: string } = {}
  ) => {
    const notYetOpen = isRoundNotYetOpen(round)
    const active = canRoundAcceptBet(round) && !notYetOpen
    const isYeekeeName = opts.isYeekeeName ?? /ยี่กี|ยี/.test((round.huayTypeName || '') + (round.huayName || ''))
    const timeClass = getLottoRoundStatusColorClass(round, active, notYetOpen, {
      active: opts.timeClass,
    })
    const closedCardClass = getLottoRoundClosedCardClass(round, active)
    const liveEntry = lookupHuayYoutubeLiveForRound(round, huayYoutubeLive)
    const badge = getRoundBadge(round, active, notYetOpen)

    return (
      <div key={key} className={CARD_COL}>
        <div className={CARD_STACK}>
          <RoundBetCard
            title={round.huayName || '-'}
            imgSrc={getRoundImageSrc(round)}
            badge={badge}
            leftLabel={notYetOpen ? 'เปิดใน' : active ? 'เวลาเหลือ' : 'สถานะ'}
            leftValue={<CountdownDisplay round={round} />}
            leftClassName={timeClass}
            rightLabel={notYetOpen ? 'เปิดรับ' : 'ปิดรับ'}
            rightValue={formatCloseTime(notYetOpen ? round.betStartTime : round.betEndTime)}
            closedCardClass={closedCardClass}
            onClick={() => handleRoundClick(round, isYeekeeName)}
            liveBadge={
              liveEntry ? (
                <HuayLiveBadge round={round} onOpen={(r) => openLivePopup(r, liveEntry)} />
              ) : null
            }
          />
        </div>
      </div>
    )
  }

  /** การ์ดรอบหวย — wip_wup เมื่อใกล้ปิดรับ */
  const renderRoundCardGrid = (
    round: LotteryRound,
    key: string,
    opts: { timeClass?: string } = {}
  ) => {
    const notYetOpen = isRoundNotYetOpen(round)
    const active = canRoundAcceptBet(round) && !notYetOpen
    const minutesRemaining = (new Date(round.betEndTime).getTime() - Date.now()) / (1000 * 60)
    const isYeekeeName = /ยี่กี|ยี/.test((round.huayTypeName || '') + (round.huayName || ''))
    const threshold = isYeekeeName ? wipWupMinutes : wipWupMinutesOther
    const isLastMinutes = active && minutesRemaining <= threshold && minutesRemaining > 0
    const timeClass = getLottoRoundStatusColorClass(round, active, notYetOpen, {
      active: opts.timeClass ?? 'c_primary',
      closed: 'c_danger',
    })
    const closedCardClass = getLottoRoundClosedCardClass(round, active)
    const liveEntry = lookupHuayYoutubeLiveForRound(round, huayYoutubeLive)
    const badge = getRoundBadge(round, active, notYetOpen)

    return (
      <div key={key} className={CARD_COL_GRID}>
        <RoundBetCard
          title={round.huayName || '-'}
          imgSrc={getRoundImageSrc(round)}
          badge={badge}
          leftLabel={notYetOpen ? 'เปิดใน' : active ? 'เวลาเหลือ' : 'สถานะ'}
          leftValue={<CountdownDisplay round={round} />}
          leftClassName={timeClass}
          rightLabel={notYetOpen ? 'เปิดรับ' : 'ปิดรับ'}
          rightValue={formatCloseTime(notYetOpen ? round.betStartTime : round.betEndTime)}
          closedCardClass={closedCardClass}
          isLastMinutes={isLastMinutes}
          onClick={() => handleRoundClick(round, isYeekeeName)}
          liveBadge={
            liveEntry ? (
              <HuayLiveBadge round={round} onOpen={(r) => openLivePopup(r, liveEntry)} />
            ) : null
          }
        />
      </div>
    )
  }

  const sectionTitle = (_imgSrc: string, label: string) => (
    <div className="col-12 px-1">
      <div className="h_huay">
        <h5 className="txt_hh c_tertiary">{label}</h5>
      </div>
    </div>
  )

  const loadingSpinner = (
    <div className="col-12 text-center py-4">
      <i className="fas fa-spinner fa-spin fs-24 c_tertiary" />
    </div>
  )

  const emptyMessage = (msg: string) => (
    <div className="col-12 text-center py-4">
      <p className="c_tertiary">{msg}</p>
    </div>
  )

  return (
    <>
      <div className="lobby-huay-listall">
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">รายการหวยวันนี้</h3>
      </div>
      {fromWinNumber && fromWinNumber.length > 0 && (
        <div className="bg_gd_quaternary border_5 p-2 mt-2 text-center">
          <small className="c_tertiary">
            <i className="fas fa-info-circle me-1" />
            คุณมีเลขจับวินรออยู่ ({fromWinNumber.length} รายการ) — กรุณาเลือกหวยที่ต้องการแทงด้านล่าง แล้วเลือกแพ็คเกจ ระบบจะนำเลขไปใส่ตารางแทงให้
          </small>
        </div>
      )}
      <div className="bg_op_secondary border_5 p-2 d-grid mt-3" style={{ rowGap: '0.75rem' }}>
        {/* หวยรัฐบาล + จับยี่กี */}
        <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
          {/* หวยรัฐบาลไทย — จาก API */}
          <div className="col-12 col-md-6 px-1 lobby-huay-col">
            <div className={CARD_STACK}>
              <div className="h_huay">
                <h5 className="txt_hh c_tertiary">หวยรัฐบาล</h5>
              </div>
              {loading ? (
                loadingSpinner
              ) : govRounds.length > 0 ? (
                (() => {
                  const round = govRounds[0]
                  const notYetOpen = isRoundNotYetOpen(round)
                  const active = canRoundAcceptBet(round) && !notYetOpen
                  const minutesRemaining = (new Date(round.betEndTime).getTime() - Date.now()) / (1000 * 60)
                  const isLastMinutes = active && minutesRemaining <= wipWupMinutesOther && minutesRemaining > 0
                  const liveEntry = lookupHuayYoutubeLiveForRound(round, huayYoutubeLive)
                  const closedCardClass = getLottoRoundClosedCardClass(round, active)
                  const countdownClass = getLottoRoundStatusColorClass(round, active, notYetOpen, {
                    active: 'c_quaternary',
                  })
                  const badge = getRoundBadge(round, active, notYetOpen)
                  return (
                    <RoundBetCard
                      title={round.huayName || 'หวยรัฐบาลไทย'}
                      imgSrc={governmentTypeImage}
                      badge={badge}
                      leftLabel={notYetOpen ? 'เปิดใน' : active ? 'เวลาเหลือ' : 'สถานะ'}
                      leftValue={<CountdownDisplay round={round} />}
                      leftClassName={countdownClass}
                      rightLabel={notYetOpen ? 'เปิดรับ' : 'ปิดรับ'}
                      rightValue={formatCloseTime(notYetOpen ? round.betStartTime : round.betEndTime)}
                      closedCardClass={closedCardClass}
                      isLastMinutes={isLastMinutes}
                      onClick={() => handleRoundClick(round, false)}
                      liveBadge={
                        liveEntry ? (
                          <HuayLiveBadge round={round} onOpen={(r) => openLivePopup(r, liveEntry)} />
                        ) : null
                      }
                    />
                  )
                })()
              ) : (
                <RoundBetCard
                  title="หวยรัฐบาลไทย"
                  imgSrc={governmentTypeImage}
                  badge={{ text: 'ปิดรับ', tone: 'closed' }}
                  leftLabel="สถานะ"
                  leftValue="ปิดรับแทง"
                  leftClassName="c_danger"
                  rightLabel="รอบ"
                  rightValue="ไม่มีรอบหวย"
                />
              )}
            </div>
          </div>

          {/* จับยี่กี — ลิงก์ไป yeekee */}
          <div className="col-12 col-md-6 px-1 lobby-huay-col">
            <div className={CARD_STACK}>
              <div className="h_huay">
                <h5 className="txt_hh c_tertiary">หวยจับยี่กี</h5>
              </div>
              <RoundBetCard
                title="หวยยี่กี VIP"
                imgSrc={yeekeeTypeImage}
                badge={{ text: 'เปิดรับ', tone: 'open' }}
                leftLabel="เวลา"
                leftValue="24 ชั่วโมง"
                leftClassName="c_danger"
                rightLabel="รอบวันนี้"
                rightValue={loading ? <i className="fas fa-spinner fa-spin" /> : `เปิดแทง ${yeekeeCount} รอบ`}
                asLink="/lobby-huay/yeekee"
              />
            </div>
          </div>
        </div>

        {/* หวยธนาคาร */}
        <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
          {sectionTitle(bankSectionImage, 'หวยธนาคาร')}
          {loading ? (
            loadingSpinner
          ) : (
            bankConfigs.map((config, idx) => {
              const round = rounds.find((r) =>
                config.searchNames.some((name) => r.huayName.includes(name))
              )
              if (!round) {
                return (
                  <div key={`bank-${idx}`} className={CARD_COL}>
                    <RoundBetCard
                      title={config.displayName}
                      imgSrc={getAssetUrl(config.img)}
                      badge={{ text: 'ปิดรับ', tone: 'closed' }}
                      leftLabel="สถานะ"
                      leftValue="ปิดรับแทง"
                      leftClassName="c_danger"
                      rightLabel="รอบ"
                      rightValue="ไม่มีรอบหวย"
                    />
                  </div>
                )
              }
              return renderRoundCard(round, round._id, {
                timeClass: 'c_success',
              })
            })
          )}
        </div>

        {/* หวยต่างประเทศ — ซ่อนทั้ง section เมื่อแอดมินปิดประเภทนี้ */}
        {!disabledHuayTypeNames.has('หวยต่างประเทศ') && (
          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
            {sectionTitle(foreignTypeImage, 'หวยต่างประเทศ')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยต่างประเทศ'] || []).length > 0 ? (
              (roundsByType['หวยต่างประเทศ'] || []).map((round) =>
                renderRoundCardGrid(round, round._id, { timeClass: 'c_primary' })
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยหุ้น VIP — ซ่อนเมื่อปิดประเภท หวยหุ้น VIP หรือ หวย VIP */}
        {!disabledHuayTypeNames.has('หวยหุ้น VIP') && !disabledHuayTypeNames.has('หวย VIP') && (
          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
            {sectionTitle(vipTypeImage, 'หวยหุ้น VIP')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยหุ้น VIP'] || roundsByType['หวย VIP'] || []).length > 0 ? (
              (roundsByType['หวยหุ้น VIP'] || roundsByType['หวย VIP'] || []).map((round) =>
                renderRoundCardGrid(round, round._id)
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยหุ้นต่างประเทศ — ซ่อนเมื่อปิดประเภท หวยหุ้น */}
        {!disabledHuayTypeNames.has('หวยหุ้น') && (
          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
            {sectionTitle(stockTypeImage, 'หวยหุ้นต่างประเทศ')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยหุ้น'] || []).filter((r) => !r.huayName.includes('ไทย')).length > 0 ? (
              (roundsByType['หวยหุ้น'] || [])
                .filter((r) => !r.huayName.includes('ไทย'))
                .map((round) => renderRoundCardGrid(round, round._id))
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยชุด — ซ่อนเมื่อปิดประเภท */}
        {!disabledHuayTypeNames.has('หวยชุด') && (
          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
            {sectionTitle(setTypeImage, 'หวยชุด')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยชุด'] || []).length > 0 ? (
              (roundsByType['หวยชุด'] || []).map((round) =>
                renderRoundCardGrid(round, round._id, { timeClass: 'c_quaternary' })
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวยหุ้นไทย — ซ่อนเมื่อปิดประเภท หวยหุ้น */}
        {!disabledHuayTypeNames.has('หวยหุ้น') && (
          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
            {sectionTitle(stockTypeImage, 'หวยหุ้นไทย')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวยหุ้น'] || []).filter((r) => r.huayName.includes('ไทย')).length > 0 ? (
              (roundsByType['หวยหุ้น'] || [])
                .filter((r) => r.huayName.includes('ไทย'))
                .map((round) => renderRoundCardGrid(round, round._id))
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* หวย VISA — ซ่อนเมื่อปิดประเภท */}
        {!disabledHuayTypeNames.has('หวย VISA') && (
          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
            {sectionTitle(visaTypeImage, 'หวย VISA')}
            {loading ? (
              loadingSpinner
            ) : (roundsByType['หวย VISA'] || []).length > 0 ? (
              (roundsByType['หวย VISA'] || []).map((round) =>
                renderRoundCardGrid(round, round._id, { timeClass: 'c_quaternary' })
              )
            ) : (
              emptyMessage('ไม่มีรอบหวยในวันนี้')
            )}
          </div>
        )}

        {/* ประเภทหวยอื่นๆ จาก API — เพิ่มประเภทใหม่ใน admin จะแสดงที่นี่โดยไม่ต้องแก้ frontend */}
        {otherTypeNames
          .filter((typeName) => !disabledHuayTypeNames.has(typeName))
          .map((typeName) => (
            <div key={typeName} className="row mx__1" style={{ rowGap: '0.75rem' }}>
              {sectionTitle(getHuayTypeImageSrc([typeName], '/img/logo-t.png'), typeName)}
              {loading ? (
                loadingSpinner
              ) : (roundsByType[typeName] || []).length > 0 ? (
                (roundsByType[typeName] || []).map((round) =>
                  renderRoundCardGrid(round, round._id, { timeClass: 'c_tertiary' })
                )
              ) : (
                emptyMessage('ไม่มีรอบหวยในวันนี้')
              )}
            </div>
          ))}
      </div>
      </div>
      <HuayLiveModal
        isOpen={livePopup !== null}
        onClose={closeLivePopup}
        title={livePopup?.title || ''}
        youtubeUrl={livePopup?.youtubeUrl || ''}
        playerStartMuted={themeConfig?.settings?.liveTvPlayerStartMuted !== false}
      />
    </>
  )
}

export default ModernLobbyHuayListAllPage
